import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * WarningTriangleFilled component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconWarningTriangleFilled extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M10.7701 1.34614C11.1404 1.11978 11.566 1 12 1C12.434 1 12.8596 1.11978 13.2299 1.34614C13.6002 1.5725 13.9009 1.89666 14.0988 2.28293L14.1023 2.28979L22.7477 19.5808C22.9281 19.9394 23.0148 20.3395 22.9979 20.7407C22.9811 21.1424 22.862 21.533 22.6516 21.8756C22.4413 22.2182 22.147 22.5013 21.7964 22.6981C21.446 22.8949 21.0509 22.9989 20.6489 23.0001H20.6465H3.35341H3.35102C2.94905 22.9989 2.55405 22.8949 2.20354 22.6981C1.85304 22.5013 1.55867 22.2182 1.34839 21.8756C1.1381 21.533 1.01889 21.1424 1.00207 20.7407C0.985262 20.3395 1.07117 19.9406 1.25163 19.582L9.89775 2.28981L9.90119 2.28292C10.0991 1.89664 10.3998 1.5725 10.7701 1.34614ZM12 7.47573C12.6511 7.47573 13.179 8.00362 13.179 8.65481V13.7642C13.179 14.4153 12.6511 14.9432 12 14.9432C11.3488 14.9432 10.8209 14.4153 10.8209 13.7642V8.65481C10.8209 8.00362 11.3488 7.47573 12 7.47573ZM13.5721 18.0874C13.5721 18.9557 12.8682 19.6596 12 19.6596C11.1317 19.6596 10.4278 18.9557 10.4278 18.0874C10.4278 17.2192 11.1317 16.5153 12 16.5153C12.8682 16.5153 13.5721 17.2192 13.5721 18.0874Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-warning-triangle-filled') === undefined) {
  customElements.define('cat-icon-warning-triangle-filled', BlocksIconWarningTriangleFilled);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-warning-triangle-filled': BlocksIconWarningTriangleFilled;
  }
}
